import {
  LayoutDashboard,
  Lightbulb,
  Settings,
  Search,
  ChevronDown,
  Globe,
  LogOut,
  User,
  MessageSquare,
  ArrowRight,
  FileText,
  Zap,
  Target,
  Activity,
  Heart,
  Filter,
  Clock,
  Wifi,
  TrendingUp,
  Rocket,
  UsersRound,
  Grid3X3,
  Scissors,
  RefreshCw,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';
import { useSettingsStore } from '@/stores/settings-store';
import { useState } from 'react';

const navItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'ai', label: 'AI Assistant', icon: MessageSquare },
  { id: 'insights', label: 'Insights', icon: Lightbulb },
  { id: 'settings', label: 'Settings', icon: Settings },
];

// Experimental views for testing different visualizations
const experimentalViews = [
  { id: 'journey', label: 'Journey', icon: ArrowRight, description: 'Search to Site Funnel' },
  { id: 'funnel', label: 'Funnel', icon: Filter, description: 'Conversion Funnel Chart' },
  { id: 'galaxy', label: 'Galaxy', icon: Rocket, description: 'Keyword Galaxy Scatter Plot' },
  { id: 'rhythm', label: 'Rhythm', icon: Clock, description: 'Audience Activity Heatmap' },
  { id: 'pulse', label: 'Pulse', icon: Wifi, description: 'Live Device Breakdown' },
  { id: 'traffic', label: 'Traffic', icon: TrendingUp, description: 'Traffic Acquisition Chart' },
  { id: 'cohort', label: 'Cohort', icon: UsersRound, description: 'User Retention Analysis' },
  { id: 'queries', label: 'Queries', icon: Search, description: 'Query Performance' },
  { id: 'pages', label: 'Pages', icon: FileText, description: 'Landing Pages' },
  { id: 'quickwins', label: 'Quick Wins', icon: Zap, description: 'SEO Opportunities' },
  { id: 'matrix', label: 'Matrix', icon: Target, description: 'Opportunity Matrix' },
  { id: 'momentum', label: 'Momentum', icon: Activity, description: 'Trend Tracker' },
  { id: 'health', label: 'Health', icon: Heart, description: 'Site Health Radar' },
  { id: 'engagement', label: 'Engagement', icon: Grid3X3, description: 'Content Performance Matrix' },
  { id: 'cannibalization', label: 'Cannibal', icon: Scissors, description: 'SEO Cannibalization Detector' },
];

// Helper to extract domain from property name or GSC site URL
function getPropertyDomain(property: { name: string; gscSite?: { siteUrl: string } }): string | null {
  // Try GSC site URL first (most reliable)
  if (property.gscSite?.siteUrl) {
    return property.gscSite.siteUrl
      .replace('sc-domain:', '')
      .replace('https://', '')
      .replace('http://', '')
      .replace(/\/$/, '');
  }
  // Try to extract domain from property name
  const name = property.name.toLowerCase().trim();
  // Check if name looks like a domain (contains a dot and common TLD)
  if (/\.(com|net|org|ai|io|co|app|dev|xyz)$/i.test(name)) {
    return name;
  }
  return null;
}

// Google favicon service URL
function getFaviconUrl(domain: string): string {
  return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
}

interface SidebarProps {
  onTogglePanel?: (panelId: string, title: string) => void;
  isAIPanelOpen?: boolean;
}

export function Sidebar({ onTogglePanel, isAIPanelOpen }: SidebarProps) {
  const { accounts, activeAccountId, properties, activePropertyId, setActiveProperty, refreshProperties, logout } =
    useAuthStore();
  const { sidebarCollapsed } = useSettingsStore();
  const [activeNav, setActiveNav] = useState('dashboard');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Determine if nav item is active (AI panel has special handling)
  const isNavActive = (itemId: string) => {
    if (itemId === 'ai') return isAIPanelOpen;
    return activeNav === itemId;
  };
  const [propertyDropdownOpen, setPropertyDropdownOpen] = useState(false);

  const handleRefreshProperties = async () => {
    setIsRefreshing(true);
    try {
      await refreshProperties();
    } finally {
      setIsRefreshing(false);
    }
  };

  const activeProperty = properties.find((p) => p.id === activePropertyId);
  const activeAccount = accounts.find((a) => a.id === activeAccountId);

  if (sidebarCollapsed) {
    return (
      <aside className="relative flex w-16 shrink-0 flex-col border-r border-border bg-surface z-20">
        {/* Collapsed navigation */}
        <nav className="flex flex-1 flex-col items-center gap-1 p-2 pt-4">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                if (item.id !== 'ai') setActiveNav(item.id);
                onTogglePanel?.(item.id, item.label);
              }}
              className={cn(
                'flex h-10 w-10 items-center justify-center rounded-lg transition-colors',
                isNavActive(item.id)
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
              title={item.label}
            >
              <item.icon className="h-5 w-5" />
            </button>
          ))}
        </nav>
      </aside>
    );
  }

  return (
    <aside className="relative flex w-64 shrink-0 flex-col border-r border-border bg-surface z-20">
      {/* Property selector */}
      <div className="relative border-b border-border p-3">
        <button
          onClick={() => setPropertyDropdownOpen(!propertyDropdownOpen)}
          className="glass-panel-sm flex w-full items-center gap-3 p-2.5 transition-colors hover:bg-muted"
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 overflow-hidden">
            {activeProperty && getPropertyDomain(activeProperty) ? (
              <img
                src={getFaviconUrl(getPropertyDomain(activeProperty)!)}
                alt=""
                className="h-5 w-5"
                referrerPolicy="no-referrer"
              />
            ) : (
              <Globe className="h-4 w-4 text-primary" />
            )}
          </div>
          <div className="flex-1 text-left">
            <p className="truncate text-sm font-medium text-foreground">
              {activeProperty?.name || 'Select Property'}
            </p>
            <p className="truncate text-xs text-muted-foreground">
              {activeProperty?.ga4Property
                ? 'GA4'
                : activeProperty?.gscSite
                  ? 'Search Console'
                  : 'No property selected'}
              {activeProperty?.ga4Property && activeProperty?.gscSite && ' + GSC'}
            </p>
          </div>
          <ChevronDown
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform',
              propertyDropdownOpen && 'rotate-180'
            )}
          />
        </button>

        {/* Property dropdown - absolute overlay */}
        {propertyDropdownOpen && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setPropertyDropdownOpen(false)}
            />
            <div className="absolute left-3 right-3 top-full mt-1 z-50 glass-panel max-h-64 overflow-y-auto shadow-lg">
              {properties.length === 0 ? (
                <div className="p-3 text-center text-sm text-muted-foreground">
                  No properties found. Make sure you have GA4 or Search Console access.
                </div>
              ) : (
                properties.map((property) => (
                  <button
                    key={property.id}
                    onClick={() => {
                      setActiveProperty(property.id);
                      setPropertyDropdownOpen(false);
                    }}
                    className={cn(
                      'flex w-full items-center gap-3 p-2.5 text-left transition-colors hover:bg-muted',
                      property.id === activePropertyId && 'bg-primary/10'
                    )}
                  >
                    <div className="flex h-5 w-5 items-center justify-center">
                      {getPropertyDomain(property) ? (
                        <img
                          src={getFaviconUrl(getPropertyDomain(property)!)}
                          alt=""
                          className="h-4 w-4"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <Globe className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 truncate">
                      <p className="truncate text-sm text-foreground">{property.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {property.ga4Property && property.gscSite
                          ? 'GA4 + GSC'
                          : property.ga4Property
                            ? 'GA4'
                            : 'GSC'}
                      </p>
                    </div>
                  </button>
                ))
              )}
              <button
                onClick={handleRefreshProperties}
                disabled={isRefreshing}
                className="flex w-full items-center gap-3 border-t border-border p-2.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
              >
                <RefreshCw className={cn('h-4 w-4', isRefreshing && 'animate-spin')} />
                {isRefreshing ? 'Refreshing...' : 'Refresh properties'}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 overflow-y-auto">
        <div className="space-y-1">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                if (item.id !== 'ai') setActiveNav(item.id);
                onTogglePanel?.(item.id, item.label);
              }}
              className={cn(
                'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors',
                isNavActive(item.id)
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
            >
              <item.icon className="h-5 w-5" />
              {item.label}
            </button>
          ))}
        </div>

        {/* Experimental Views Section */}
        <div className="mt-6 pt-4 border-t border-border">
          <p className="px-3 text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
            Experiments
          </p>
          <div className="space-y-0.5">
            {experimentalViews.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setActiveNav(item.id);
                  onTogglePanel?.(item.id, item.label);
                }}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                  activeNav === item.id
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                )}
                title={item.description}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* User section */}
      <div className="border-t border-border p-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted overflow-hidden">
            {activeAccount?.picture ? (
              <img
                src={activeAccount.picture}
                alt={activeAccount.name || ''}
                className="h-9 w-9 rounded-full object-cover"
                referrerPolicy="no-referrer"
                crossOrigin="anonymous"
              />
            ) : (
              <User className="h-5 w-5 text-muted-foreground" />
            )}
          </div>
          <div className="flex-1 truncate">
            <p className="truncate text-sm font-medium text-foreground">
              {activeAccount?.name || 'User'}
            </p>
            <p className="truncate text-xs text-muted-foreground">
              {activeAccount?.email || ''}
            </p>
          </div>
          <button
            onClick={() => logout()}
            className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            title="Sign out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}
